// All site copy sourced from Gaatha's own provided content.
export const NAV = [
  { label: "The Gap", href: "#gap" },
  { label: "Approach", href: "#approach" },
  { label: "Offerings", href: "#offerings" },
  { label: "Work", href: "#work" },
  { label: "About", href: "#about" },
  { label: "Contact", href: "#contact" },
];

export const HERO = {
  brand: "Gaatha",
  headlineLines: ["Communication strategy", "for mission-led", "organisations"],
  subhead:
    "I help nonprofits, foundations, CSR teams and other mission-led organisations bring clarity and structure to communication that has become scattered as they grow or change.",
  cta: "Start a conversation",
  tagline: "Let's make your story easier to tell.",
};

export const GAP = {
  headingLines: ["When communication", "stops keeping pace"],
  problems: [
    "The organisation is introduced differently across decks, proposals and meetings.",
    "The website, reports and presentations do not always tell the same story.",
    "Different team members describe the organisation in different ways.",
    "The communication calendar is full, but not always connected to organisational priorities.",
  ],
  solution:
    "Gaatha brings these pieces together—clarifying the story, setting priorities and creating communication tools the team can continue using.",
};

export const APPROACH = {
  headingLines: [
    "Four ways I help bring",
    "communication into focus",
  ],
  intro:
    "I bring senior judgment and practical structure to four areas of communication.",
  blocks: [
    {
      title: "Clarify the story",
      text:
        "Strengthen how your organisation, programmes and impact are explained across websites, decks, reports and stakeholder communication.",
    },
    {
      title: "Set communication priorities",
      text:
        "Decide what needs to be communicated, to whom and when—across donors, partners, teams, leadership, campaigns and key organisational moments.",
    },
    {
      title: "Strengthen stories and proof",
      text:
        "Identify the stories, data, photographs, testimonials and proof points your organisation needs to collect and use more consistently.",
    },
    {
      title: "Equip your team",
      text:
        "Create practical structures, templates and guidance that internal teams, writers, designers and partner agencies can understand and execute.",
    },
  ],
};

export const OFFERINGS = {
  headingLines: ["How I can help"],
  intro:
    "I can support a broad communication need or a focused organisational priority.",
  groups: [
    {
      category: "Focused Communication Assignments",
      description: "Start with a single assignment, based on your immediate communication need.",
      cards: [
        {
          title: "Content and Stories",
          description: "Help your audiences understand your work through clear content and compelling stories.",
          list: [
            "Social media content batches",
            "Newsletters",
            "Leadership articles",
            "Case studies and impact stories",
          ],
        },
        {
          title: "Presentations and Communication Materials",
          description: "Make your work, results and funding needs easy to understand and share.",
          list: [
            "Fundraising and programme presentations",
            "Donor reports",
            "Brochures and leaflets",
            "Campaign materials",
            "Annual report content and editing",
          ],
        },
      ],
    },
    {
      category: "Strategic Communication Support",
      description: "Bring clarity and direction to how your organisation communicates.",
      cards: [
        {
          title: "Communication Reviews and Strategy",
          description: "Identify what needs attention and sharpen how your organisation communicates its purpose, work and value.",
          list: [
            "Communication reviews",
            "Core organisational story and key messages",
            "Fundraising communication strategy",
            "Stakeholder communication strategy",
          ],
        },
        {
          title: "Communication Planning and Campaigns",
          description: "Turn communication priorities into practical plans and purposeful campaigns.",
          list: [
            "Communication plans",
            "Campaigns and launches",
            "Content priorities and calendars",
            "Team guidance and communication tools",
            "Film concepts and creative direction",
            "Photography direction",
            "Influencer and brand-partner identification",
          ],
        },
      ],
    },
  ],
};

export const OUTCOMES = {
  headingLines: [
    "Better communication",
    "should change how the",
    "organisation works—not",
    "just how the words sound.",
  ],
  intro:
    "The value of Gaatha is not another polished document that sits unused. It is communication direction your organisation can apply repeatedly.",
  items: [
    {
      title: "One shared story",
      text: "Teams and leaders can explain the organisation consistently.",
    },
    {
      title: "Clearer fundraising communication",
      text: "Stronger asks and material that makes funding needs easier to understand.",
    },
    {
      title: "Less rebuilding from scratch",
      text: "Stories, data, photographs and proof can be reused across formats.",
    },
    {
      title: "Clearer direction for teams",
      text: "People know what to communicate, when it matters and who will take it forward.",
    },
  ],
};

export const CLIENTS = {
  heading: "Who I work best with",
  paras: [
    "I work with nonprofits, foundations, CSR teams and other mission-led organisations tackling complex social and environmental challenges.",
    "This includes work across education, healthcare and mental health, livelihoods, inclusion, climate and sustainability, renewable energy, responsible consumption, philanthropy and CSR.",
  ],
};

export const WORK = {
  heading: "Selected Work",
  intro:
    "A few examples of how I help organisations bring greater clarity, structure and direction to their communication.",
  cases: [
    {
      name: "Shiksharth",
      url: "https://shiksharth.in/",
      type: "Education nonprofit · Donor communication review",
      brief:
        "Make Shiksharth’s donor communication clearer and more useful for fundraising. Strong field evidence and stories existed, but were scattered across documents, with inconsistent messaging and funding asks that needed sharpening.",
      delivered:
        "A review of donor-facing materials and a prioritised roadmap for a reusable donor communication kit, covering the core story, funding options, impact evidence and proposal structure, with clear recommendations for team ownership and implementation.",
    },
    {
      name: "Sheradil Foundation",
      url: "https://www.sheradil.org/",
      type: "Child-protection foundation · Communication planning",
      brief:
        "Help Sheradil build visibility, trust and fundraising support with limited communication capacity. Outreach relied largely on personal networks and immediate needs, calling for a more consistent approach that protected the privacy and dignity of children and survivors.",
      delivered:
        "An annual communication blueprint organised into quarterly actions for donors, schools, partners, volunteers and the public. It included a monthly checklist, funding options, team responsibilities and a 30-day starting plan—giving the team practical priorities suited to its capacity.",
    },
  ],
};

export const ABOUT = {
  heading: "A senior practitioner with both sides of experience",
  image: "/aarti.jpg",
  quote:
    "I lead Gaatha, an independent communication strategy practice for mission-led organisations. Over the years, I have led communication mandates at The Akshaya Patra Foundation and Cipla Foundation, and consulted with organisations including The Drama School Foundation and Jai Vakeel Foundation. My work has spanned CSR communication, fundraising campaigns, stakeholder engagement, digital strategy, internal communication and impact storytelling.",
  paras: [
    "I also bring corporate-sector experience from ITC, Eureka Forbes, Reliance Life Insurance, Aircel and NIIT, across brand strategy, product marketing, digital communication, customer acquisition, retail marketing and campaign planning.",
    "I am a senior marketing and communications professional with over 19 years of experience across the corporate, CSR, philanthropy and nonprofit sectors.",
    "My work combines strategic thinking with practical direction, with a focus on communication that teams can understand, use and carry forward.",
  ],
};

export const CONTACT = {
  heading: "Not sure what your organisation needs?",
  intro:
    "Tell me what is changing, what feels unclear or what important communication moment you are preparing for. We can begin with a conversation.",
  cta: "Start a conversation",
  email: "aartikn@gmail.com",
  linkedin: "https://www.linkedin.com/in/aartikrishnan31/",
  location: "Mumbai, India",
};

export const MEDIA = {
  // Editorial band — user-provided composite (photo + baked tagline).
  band:
    "/easy-story.webp",
  // Who I work with — collaboration in the field.
  clients:
    "https://images.unsplash.com/photo-1633410195091-bd66114cef5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxzb2NpYWwlMjB3b3JrZXJzJTIwZmllbGQlMjBydXJhbCUyMGluZGlhfGVufDB8fHx8MTc4NDAxMTE5N3ww&ixlib=rb-4.1.0&q=85",
};

export const MARQUEE_WORDS = [
  "Clarity",
  "Direction",
  "Story",
  "Structure",
  "Proof",
  "Consistency",
];
